/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/
// react
import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  Link,
  useParams,
  useNavigate,
  useLocation,
  Outlet,
} from "react-router-dom";
// redux
import { useDispatch, useSelector } from "react-redux";
// actions
import useInstance from "../../../actions/useInstance";
import { getInstance } from "../../../actions";
// BASE components
import BASEPageSimple from "../../../../@BASE/core/BASEPageSimple";
import BASELoading from "../../../../@BASE/core/BASELoading";
import BASESvgIcon from "../../../../@BASE/core/BASESvgIcon";
import useThemeMediaQuery from "../../../../@BASE/hooks/useThemeMediaQuery";
import NavLinkAdapter from "../../../../@BASE/core/NavLinkAdapter";
import { updateNavigationItem } from "../../../store/BASE/navigationSlice";
// material-ui
import { styled, useTheme, lighten } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";
import clsx from "clsx";
// custom components
import PageHeader from "./views/PageHeader";
import GUIHeader from "./preview/gui/GUIHeader";
import GUIContent from "./preview/gui/GUIContent";
import GUIFooter from "./preview/gui/GUIFooter";

const Root = styled(BASEPageSimple)(({ theme }) => ({
  "& .BASEPageSimple-header": {},
  "& .BASEPageSimple-toolbar": {},
  "& .BASEPageSimple-content": {},
  "& .page-header": {
    backgroundColor: theme.palette.background.royalBlue,
    color: theme.palette.getContrastText(theme.palette.primary.main),
  },
  "& .BASEPageSimple-sidebarHeader": {},
  "& .BASEPageSimple-sidebarContent": {},
}));

function PageLivePreview(props) {
  const dispatch = useDispatch();
  const location = useLocation();
  const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down("lg"));
  const theme = useTheme();
  const routeParams = useParams();
  const navigate = useNavigate();

  const {
    instance,
    instanceDetails,
    handleInstanceAction,
    loading,
    error,
    setError,
  } = useInstance("pages", routeParams.id, "view");
  const [section, setSection] = useState(null);
  const [reviews, setReviews] = useState([]);

  const [leftSidebarOpen, setLeftSidebarOpen] = useState(!isMobile);
  const [rightSidebarOpen, setRightSidebarOpen] = useState(false);

  const [languageToggle, setLanguageToggle] = useState(false);

  const handleLanguageToggle = (event) => {
    setLanguageToggle(event.target.checked);
  };

  /// get data from API on page load
  const getAllPages = async () => {
    try {
      const response = await dispatch(
        getInstance("sections")(instance?.section?.id)
      ).unwrap();
      setSection(response);
      setReviews(response?.lesson?.reviews);
    } catch (err) {
      console.log(err);
    }
  };

  // Generate a random number between 1 and 5, but only once
  const randomImageNumber = useMemo(
    () => Math.floor(Math.random() * 5) + 1,
    []
  );

  // Construct the background image URL
  const backgroundImageUrl = useMemo(() => {
    const acronym =
      instance?.section?.lesson?.course?.language.acronym?.toUpperCase();
    const label = instance?.section?.lesson?.label;

    return `https://base-media-repo.web.app/${acronym}/${label}/images/backgrounds/background_${randomImageNumber}.jpg`;
  }, [instance, randomImageNumber]);

  useEffect(() => {
    if (instance) {
      getAllPages();
      dispatch(
        updateNavigationItem("course-component", {
          url: `courses/${instance?.section?.lesson?.course?.id}`,
          disabled: false,
        })
      );
      dispatch(
        updateNavigationItem("lesson-component", {
          url: `lessons/${instance?.section?.lesson?.id}`,
          disabled: false,
        })
      );
      dispatch(
        updateNavigationItem("section-component", {
          url: `sections/${instance.section.id}`,
          disabled: false,
        })
      );
      dispatch(
        updateNavigationItem("page-component", {
          url: `pages/${routeParams.id}`,
          disabled: false,
        })
      );
    }
  }, [dispatch, instance]);

  useEffect(() => {
    setLeftSidebarOpen(!isMobile);
    if (location.pathname.includes("/comments")) {
      setRightSidebarOpen(true);
    } else {
      setRightSidebarOpen(false);
    }
  }, [isMobile, location.pathname]);

  if (!instance) {
    return <BASELoading />;
  }

  return (
    <Root
      content={
        <div className="w-full">
          <div className="page-header w-full">
            <PageHeader
              instance={instance}
              details={instanceDetails}
              resource="pages"
              action="view"
              onSave={null}
              save_disabled
              leftSidebarToggle={(ev) => {
                setLeftSidebarOpen(!leftSidebarOpen);
              }}
              livePreview
            />
          </div>
          <div className="flex flex-col justify-center p-16 pb-64 sm:p-24 sm:pb-64 md:p-24 md:pt-0 md:pb-64">
            <div
              className="flex flex-col w-full mx-auto"
              style={{ maxWidth: "1366px" }}
            >
              <GUIHeader
                lesson={instance?.section?.lesson?.title}
                lang={instance?.section?.lesson?.course?.language.acronym}
                languageToggle={languageToggle}
                handleLanguageToggle={handleLanguageToggle}
              />

              <div
                style={{
                  position: "relative",
                  width: "100%",
                  paddingBottom: "calc(56.25% - 138px)",
                  overflow: "hidden",
                  backgroundImage: `url("${backgroundImageUrl}")`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    maxWidth: "100%",
                    width: "100%",
                    height: "100%",
                    overflowY: "auto",
                    overflowX: "hidden",
                    backgroundColor: "rgba(0, 0, 0, 0.4)",
                    backdropFilter: "blur(4px)",
                  }}
                >
                  <GUIContent
                    obj={instance.content}
                    label={instance?.template?.label}
                    rtl={instance?.section?.lesson?.course?.language?.rtl}
                    ins={instance?.template?.instructions}
                    course={instance?.section?.lesson?.course}
                    comments={instance?.comments}
                    languageToggle={languageToggle}
                  />
                </div>
              </div>

              {/* <GUIFooter pages={getPagesByLessonId(page.lesson_id)} currentPage={{id: page.id, title: page.title, section: getSectionById(page.section_id)?.title}} /> */}
              <GUIFooter
                currentPage={{
                  id: instance.id,
                  title: instance.title,
                  section: instance?.section?.title,
                }}
                instance={instance}
              />
            </div>
          </div>
        </div>
      }
      leftSidebarOpen={leftSidebarOpen}
      leftSidebarOnClose={() => {
        setLeftSidebarOpen(false);
      }}
      leftSidebarWidth={300}
      leftSidebarContent={null}
      rightSidebarContent={<Outlet context={{ instance, reviews }} />}
      rightSidebarOpen={rightSidebarOpen}
      rightSidebarOnClose={() => setRightSidebarOpen(false)}
      rightSidebarWidth={400}
      scroll={isMobile ? "normal" : "content"}
    />
  );
}

export default PageLivePreview;
